import React, { useContext, useEffect, useMemo, useRef } from 'react';

import clsx from 'clsx';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { FiArrowUp, FiMic, FiSquare } from 'react-icons/fi';

import { AIChatContext } from '../../context';
import { useLailaVoice, LailaVoicePhase } from '../../hooks/useLailaVoice';
import { voiceSupported } from '../../hooks/useVoiceRecorder';
import { LailaCard } from '../cards/LailaCard';
import { LailaCardsContext } from '../cards/LailaCardsContext';
import { EMPLOYER_SUGGESTIONS, WORKER_SUGGESTIONS } from '../LailaWelcome';
import { LailaOrb, LailaOrbState } from '../launcher/LailaOrb';

const ORB_STATE: Record<LailaVoicePhase, LailaOrbState> = {
  idle: 'idle',
  listening: 'listening',
  transcribing: 'thinking',
  thinking: 'thinking',
  speaking: 'speaking',
};

const PROBLEMS = {
  mic: 'Laila needs your microphone. Allow it in your browser settings.',
  unheard: "I didn't catch that. Tap the mic and try again.",
  failed: "Laila couldn't answer that.",
  mute: "Couldn't play Laila's voice, so here's her answer.",
};

const formatSeconds = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

interface Props {
  // Back to the chat, to type instead.
  onType: () => void;
  orbSize?: number;
}

// Voice mode: tap the mic, ask, and Laila answers out loud. The questions and
// answers land in the same conversation as the chat.
export const LailaVoice: React.FC<Props> = ({ onType, orbSize = 132 }) => {
  const classes = useStyles();
  const { user, setVoice } = useContext(AIChatContext);
  const voice = useLailaVoice();
  // A tap on a card's answer is the next question, spoken back like any other.
  const cardActions = useContext(LailaCardsContext);
  const askVoice = useRef(voice.ask);
  askVoice.current = voice.ask;
  const voiceCardActions = useMemo(
    () => ({ ...cardActions, answer: (text: string) => askVoice.current(text) }),
    [cardActions]
  );
  const canRecord = useMemo(voiceSupported, []);
  const { phase, problem } = voice;
  const busy = phase === 'transcribing' || phase === 'thinking';
  const suggestions = (user?.positions?.length ? EMPLOYER_SUGGESTIONS : WORKER_SUGGESTIONS).slice(0, 3);

  // Short spoken answers while this is open.
  useEffect(() => {
    setVoice(true);
    return () => setVoice(false);
  }, [setVoice]);

  // Esc stops Laila first; once she's quiet it closes the panel as usual.
  const stop = useRef(voice.stop);
  stop.current = voice.stop;
  useEffect(() => {
    if (phase === 'idle') {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        stop.current();
      }
    };
    // Capture, so this runs before the panel's own Esc handler.
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [phase]);

  const status = problem
    ? PROBLEMS[problem]
    : phase === 'listening'
    ? `Listening… ${formatSeconds(voice.seconds)}`
    : phase === 'transcribing'
    ? 'Got it…'
    : phase === 'thinking'
    ? 'Thinking…'
    : phase === 'speaking'
    ? 'Tap the mic to cut in'
    : !canRecord
    ? "This browser can't record. Pick a question below."
    : voice.question
    ? 'Tap the mic to ask more'
    : 'Tap the mic and ask';

  const mic =
    phase === 'listening' ? (
      <ButtonBase
        className={clsx(classes.mic, classes.micLive)}
        style={{ ['--level' as string]: voice.level }}
        onClick={voice.finish}
        aria-label="Done, ask Laila">
        <FiArrowUp size={28} />
      </ButtonBase>
    ) : busy ? (
      <ButtonBase className={clsx(classes.mic, classes.micBusy)} disabled aria-label="Laila is working on it">
        <CircularProgress size={26} color="inherit" />
      </ButtonBase>
    ) : (
      <ButtonBase
        className={classes.mic}
        onClick={voice.listen}
        disabled={!canRecord}
        aria-label={phase === 'speaking' ? 'Interrupt and ask' : 'Ask Laila by voice'}>
        <FiMic size={28} />
      </ButtonBase>
    );

  return (
    <Box className={classes.root}>
      <Box className={classes.stage}>
        <LailaOrb size={orbSize} state={ORB_STATE[phase]} level={voice.level} className={classes.orb} />

        <Typography
          className={clsx(classes.status, { [classes.statusLive]: phase !== 'idle' && !problem })}
          role="status"
          aria-live="polite">
          {status}
        </Typography>

        {problem === 'failed' && (
          <Button color="primary" onClick={voice.tryAgain} className={classes.retry}>
            Try again
          </Button>
        )}

        {voice.question ? (
          <Box className={classes.turn} key={voice.question}>
            <Typography className={classes.question}>{voice.question}</Typography>
            {voice.answer && <Typography className={classes.answer}>{voice.answer}</Typography>}
            <LailaCardsContext.Provider value={voiceCardActions}>
              {voice.cards.map(card => (
                <Box key={card.id} className={classes.card}>
                  <LailaCard card={card} />
                </Box>
              ))}
            </LailaCardsContext.Provider>
          </Box>
        ) : (
          phase === 'idle' && (
            <Box className={classes.chips}>
              {suggestions.map(suggestion => (
                <Chip
                  key={suggestion}
                  label={suggestion}
                  clickable
                  onClick={() => voice.ask(suggestion)}
                  className={classes.chip}
                />
              ))}
            </Box>
          )
        )}
      </Box>

      <Box className={classes.controls}>
        <Tooltip title="Type instead">
          <ButtonBase className={classes.side} onClick={onType} aria-label="Type instead">
            <span className={classes.aa}>Aa</span>
          </ButtonBase>
        </Tooltip>

        {mic}

        <Tooltip title={phase === 'idle' ? '' : 'Stop (Esc)'}>
          <span>
            <ButtonBase
              className={classes.side}
              onClick={voice.stop}
              disabled={phase === 'idle'}
              aria-label="Stop">
              <FiSquare size={16} className={classes.stopIcon} />
            </ButtonBase>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => {
  const brand = theme.palette.type === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main;

  return createStyles({
    root: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    stage: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '0 16px 24px',
      textAlign: 'center',
    },
    // Room above and below for the halo, which reaches well past the orb and swells with the voice.
    orb: { margin: '72px 0 52px', flex: 'none' },
    status: {
      fontSize: 13,
      fontWeight: 500,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
      minHeight: 20,
      fontVariantNumeric: 'tabular-nums',
    },
    statusLive: { color: theme.palette.primary.main },
    retry: { marginTop: 8 },
    chips: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 10,
      marginTop: 28,
      animation: '$enter 320ms cubic-bezier(.2,.8,.2,1) both',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    chip: {
      height: 'auto',
      maxWidth: '100%',
      padding: '9px 6px',
      borderRadius: 20,
      fontSize: 15,
      fontWeight: 500,
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.action.hover,
      '& .MuiChip-label': { whiteSpace: 'normal' },
      '&:hover, &:focus': { backgroundColor: theme.palette.action.selected },
    },
    turn: {
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
      width: '100%',
      maxWidth: 360,
      marginTop: 24,
      animation: '$enter 320ms cubic-bezier(.2,.8,.2,1) both',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    question: {
      fontSize: 15,
      color: theme.palette.text.secondary,
      '&::before': { content: '"“"' },
      '&::after': { content: '"”"' },
    },
    answer: {
      fontSize: 18,
      lineHeight: 1.5,
      fontWeight: 500,
      color: theme.palette.text.primary,
      animation: '$enter 320ms cubic-bezier(.2,.8,.2,1) both',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    // A card reads like it does in the chat: left-aligned, the turn's full width.
    card: {
      alignSelf: 'stretch',
      textAlign: 'left',
      animation: '$enter 320ms cubic-bezier(.2,.8,.2,1) both',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    controls: {
      flex: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 28,
      padding: '16px 16px 20px',
      paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    side: {
      width: 52,
      height: 52,
      borderRadius: '50%',
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.action.hover,
      transition: 'background-color 150ms, opacity 150ms',
      '&:hover': { backgroundColor: theme.palette.action.selected },
      '&.Mui-disabled': { opacity: 0.4 },
    },
    aa: { fontSize: 16, fontWeight: 600 },
    stopIcon: { fill: 'currentColor' },
    mic: {
      width: 72,
      height: 72,
      borderRadius: '50%',
      color: theme.palette.common.white,
      backgroundColor: brand,
      boxShadow: `0 8px 24px ${alpha(brand, 0.35)}`,
      transition: 'background-color 150ms, box-shadow 120ms linear, transform 150ms',
      '&:hover': { backgroundColor: theme.palette.primary.dark },
      '&:active': { transform: 'scale(.96)' },
      '&.Mui-disabled': {
        color: theme.palette.action.disabled,
        backgroundColor: theme.palette.action.disabledBackground,
        boxShadow: 'none',
      },
    },
    micBusy: {
      '&.Mui-disabled': { color: theme.palette.common.white, backgroundColor: alpha(brand, 0.6) },
    },
    // A ring that swells with the speaker's voice.
    micLive: {
      boxShadow: `0 0 0 calc(4px + var(--level, 0) * 18px) ${alpha(brand, 0.18)}`,
    },
    '@keyframes enter': {
      from: { opacity: 0, transform: 'translateY(8px)' },
      to: { opacity: 1, transform: 'none' },
    },
  });
});
